<script lang="ts">
	import { browser } from '$app/environment';
	import Icon from '@components/Icon.svelte';
	import LoadingCircle from '@components/LoadingCircle.svelte';
	import ActionClose from '@icons/ActionClose.svelte';
	import ActionSearch from '@icons/ActionSearch.svelte';
	import { createEventDispatcher } from 'svelte';

	export let value: string;
	export let label: string;
	export let hint: string | undefined = undefined;
	export let id: string;
	export let disabled: boolean = false;
	export let required: boolean = false;
	export let isLoading: boolean = false;
	let className = '';
	export { className as class };

	let lastSearchedValue = '';

	const dispatch = createEventDispatcher<{
		search: { value: string };
		focus: Record<string, never>;
	}>();

	function onButtonClick() {
		lastSearchedValue = value;
		dispatch('search', { value });
	}

	function handleInput(event: Event) {
		if ((event as KeyboardEvent).key === 'Enter') {
			lastSearchedValue = value;
			dispatch('search', { value });
		}
		if ((event as KeyboardEvent).key === 'Escape') {
			resetValue();
		}
	}

	function handleFocus() {
		// scrolling to index page search-input on focus to be able to see event list updating
		if (browser) {
			const indexPageSearchTermInput = document?.querySelector('#search-term-input');

			if (indexPageSearchTermInput) {
				indexPageSearchTermInput.scrollIntoView({ behavior: 'smooth' });
			}
		}
		dispatch('focus', {});
	}

	function resetValue() {
		value = '';
		if (value !== lastSearchedValue) {
			dispatch('search', { value });
		}
	}
</script>

<div id={`${id}-2`}></div>
<div class="flex-grow {className}">
	<div class="input-wrapper" class:disabled>
		<label class="sr-only" for={id}>
			{label}
		</label>
		<input
			{id}
			on:focus={handleFocus}
			on:blur
			on:keydown={handleInput}
			class="input-element peer"
			type="text"
			bind:value
			{disabled}
			{required}
			{...$$restProps}
		/>

		{#if isLoading}
			<div class="absolute right-21 top-1/2 -translate-y-1/2" class:!right-15={value?.length === 0}>
				<LoadingCircle />
			</div>
		{/if}

		{#if value && value.length > 0}
			<button
				class="delete-button"
				disabled={false}
				on:click={resetValue}
				title="Suche zurücksetzen"
			>
				<Icon component={ActionClose} class="text-gray-1" />
			</button>
		{/if}

		<button class="search-button" disabled={false} on:click={onButtonClick} title="Suchen">
			<Icon component={ActionSearch} class="text-gray-1" />
		</button>
	</div>
	{#if hint}<p class="mt-1.5 text-additional font-bold">{hint}</p>{/if}
</div>

<style lang="postcss">
	.input-wrapper {
		@apply box-border
		overflow-hidden
		flex-grow
		h-13.5
		w-full
		align-top
		inline-block
		transition-all
		ease-in-out
		duration-200
		relative
		bg-white
		border
		border-gray-4
		dark:border-gray-7
		rounded;
	}

	.input-wrapper.disabled {
		@apply pointer-events-none 	opacity-50;
	}

	.input-element {
		@apply font-normal
    focus:ring-0
    text-lg
    text-gray-1
	placeholder:text-lg
	placeholder:font-normal
	placeholder:text-gray-1
    dark:text-white
    w-full
    h-full
    px-3	
    bg-transparent
    rounded;
	}

	.delete-button {
		@apply flex items-center justify-center w-12 h-full absolute right-12 top-0;
	}

	.search-button {
		@apply flex items-center justify-center w-12 h-full bg-gray-10 hover:bg-gray-11 absolute right-0 top-0;
	}

	.search-button svg {
		@apply w-[1.125rem] h-[1.125rem];
	}

	.error {
		@apply text-function-red;
	}
</style>
